import { IGetBfixJobs, IGetJobs, Job, State, UrlData } from '@/models/main';

import { MutationTree } from 'vuex';
import { format } from 'date-fns-tz';
import store from '@/store';

export const mutations: MutationTree<State> = {
  SET_JOB_DATA(state: State, val: IGetJobs) {
    state.jobData = {
      all: [...val.cancelled, ...val.completed, ...val.in_progress, ...val.unallocated],
      ...val,
    };
  },

  CLEAR_JOB_DATA(state: State) {
    state.jobData = {
      all: [],
      cancelled: [],
      completed: [],
      in_progress: [],
      unallocated: [],
    };
  },

  SET_ROUTER_DATA(state: State, routeObj: UrlData) {
    state.routerData.prevUrl = routeObj;
  },

  SET_JOB_BFIX_DATA(state: State, val: IGetBfixJobs) {
    state.jobBfixData = {
      all: [...val.cancelled, ...val.completed, ...val.in_progress, ...val.unallocated],
      ...val,
    };
  },

  ORDER_JOBS(state: State) {
    // Check for mark as done
    state.jobData.in_progress.forEach((job, idx) => {
      if (job.is_done) {
        state.jobData.in_progress.splice(idx, 1);
        state.jobData.completed.unshift(job);
      }
    });
    state.jobData.completed.forEach((job, idx) => {
      if (!job.is_done) {
        if (
          job.notes &&
          job.notes[job.notes.length - 1] &&
          job.notes[job.notes.length - 1].note === 'The job was returned'
        ) {
          state.jobData.completed.splice(idx, 1);
          state.jobData.in_progress.unshift(job);
        }
      }
    });
    // sorting jobs
    for (const key in state.jobData) {
      if (Object.prototype.hasOwnProperty.call(state.jobData, key)) {
        state.jobData[key].sort((a: Job, b: Job): number => {
          if (key === 'all') {
            const jobsWithRequest4Change = state.jobData[key].filter((job: Job) => job.is_rfc);
            state.storeJobsWithRequest4Change = jobsWithRequest4Change;
          }
          // TODO: fix to casl $abitily
          const isStaff = store.getters['user/isStaff'];
          return (
            +isRed(b) - +isRed(a) ||
            +isYellow(b, isStaff) - +isYellow(a, isStaff) ||
            +b.priority - +a.priority ||
            +!isMainStopArrived(b) - +!isMainStopArrived(a)
          );
        });
      }
    }
  },

  ADD_NOTE(state: State, params) {
    const jobInProgress: Job | undefined = state.jobData.in_progress.find(
      (item: Job) => item.id === params.job_id && item.job_date === params.job_date,
    );
    const jobInCompleted: Job | undefined = state.jobData.completed.find(
      (item: Job) => item.id === params.job_id && item.job_date === params.job_date,
    );
    const job = jobInProgress || jobInCompleted;

    if (job && jobInProgress) {
      job.edited = 1;
    }
    if (job && jobInCompleted) {
      job.edited = 2;
    }
    if (job) {
      job.notes.push({
        note: params.note,
        required1: params.required1,
        required2: params.required2,
        // date in Sydney tz, example: 2021-12-02T11:42:22.203+11:00
        write_date: format(new Date(), "yyyy-MM-dd'T'HH:mm:ssxxx", { timeZone: 'Australia/Sydney' }),
        write_user: params.created_by,
      });
      job.required1 = params.required1;
      job.required2 = params.required2;
      job.is_done = params.is_done;
      store.dispatch('orderJobs');
    }
  },

  SET_SEARCH(state: State, search) {
    state.storeSearch = search;
  },
  SET_CATEGORY(state: State, category) {
    state.storeShowCategory = category;
  },
  SET_BFIX_CATEGORY(state: State, category) {
    state.storeShowBfixCategory = category;
  },
  SET_SELECTED_STATES(state: State, states) {
    state.storeSelectedStates = states;
  },
  SET_DRIVERS_SELECT(state: State, drivers) {
    state.storeDrivers = drivers;
  },
  SET_SIDE_MENU_OPEN(state: State, val) {
    state.isSideMenuOpen = val;
  },
  SET_GLOBAL_LOADER(state: State, val) {
    state.isLoadingGlobal = val;
  },
  SET_ERROR(state: State, error) {
    state.error = error;
  },
  SET_NETWORK_ERROR(state: State, error) {
    state.errors.networkError = error;
  },
  SET_LOGIN_ERROR(state: State, error) {
    state.errors.loginError = error;
  },
  SET_JOB_COMPLETE(state: State, params) {
    const job: Job | undefined = state.jobData.in_progress.find((item: Job) => item.job_key === params.job_key);
    if (!job) {
      return;
    }
    const jobIndex = state.jobData.in_progress.findIndex(item => item.job_key === params.job_key);
    state.jobData.in_progress.splice(jobIndex, 1);
    state.jobData.completed.unshift(job);
  },
  UPDATE_JOB_IN_PROGRESS_STATUS(state: State, params) {
    const jobIndex = state.jobData.in_progress.findIndex(item => item.job_key === params.job_key);
    state.jobData.in_progress[jobIndex] = params;
  },
  TOGGLE_JOB_REQUEST_FOR_CHANGE(state: State, params) {
    const jobIndex = state.jobData.in_progress.findIndex(item => item.job_key === params.rockhopper_key);
    if (state.jobData.in_progress[jobIndex]) {
      state.jobData.in_progress[jobIndex].is_rfc = !params.is_rfc;
    }
  },
};

function isRed(job: Job): boolean {
  return (
    job.delay === 2 ||
    (job.equipment_code1 === '' &&
      !job.allocated &&
      !job.arrived1 &&
      format(new Date(), 'yyyy-MM-dd') === format(new Date(job.required2), 'yyyy-MM-dd')) ||
    job.status === 6
  );
}

function isYellow(job: Job, isStaff: boolean): boolean {
  return job.delay === 1 && isStaff && (job.status === 1 || job.status === 2);
}

function isMainStopArrived(job: Job): boolean {
  if (job.arrived2) {
    return true;
  }
  // Check for service code **PO, **IT, **ST. If there is such a code, then the main stop is Stop1.
  // For else codes the main stop is Stop2.
  for (const item of job.services) {
    if (item.search(/\b\w\wPO\b/) !== -1 || item.search(/\b\w\wIT\b/) !== -1 || item.search(/\b\w\wST\b/) !== -1) {
      return true;
    }
  }
  return false;
}
